import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useOutletContext, useParams } from "react-router-dom";
import { BookOpen, Calculator, FlaskConical, FileUp, ArrowDown, Square, Upload, MessageCircleQuestion } from "lucide-react";
import { WelcomeMascot } from "@/components/Mascot";
import { toast } from "sonner";
import { http, streamChat, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Composer } from "@/components/Composer";
import { AssistantMessage, UserMessage } from "@/components/ChatMessage";
import { ChatActions } from "@/components/ChatActions";
import { ExamCountdown } from "@/components/ExamCountdown";
import { logWarn } from "@/lib/log";
import { newId, isStopped, stoppedToast } from "@/lib/aiJob";
import { VIDEO_URL } from "@/lib/video";
import { useVideoFlow, VideoConfirmDialog } from "@/components/VideoFlow";

const STARTERS = [
  { icon: Calculator, label: "Solve an equation", text: "Solve 2x² − 7x + 3 = 0 and explain each step" },
  { icon: FlaskConical, label: "Explain science", text: "Why does ice float on water?" },
  { icon: BookOpen, label: "Revise for an exam", text: "Summarize the causes of World War I for my exam" },
  { icon: FileUp, label: "Quiz me on notes", text: "I'll upload my class notes — quiz me on them" },
];

function Empty({ onPick, name }) {
  const fire = (ev) => window.dispatchEvent(new Event(ev));
  return (
    <div className="max-w-3xl mx-auto w-full px-4 sm:px-6 pt-[4vh] sm:pt-[9vh] pb-6">
      <div className="flex items-center gap-4 sm:gap-6">
        <div className="min-w-0 flex-1">
          <p className="text-sm text-sky-300 mb-2 rise">Hi {name?.split(" ")[0]}</p>
          <h1 className="font-display text-3xl sm:text-5xl font-semibold tracking-tight leading-tight rise" style={{ animationDelay: "40ms" }}>
            What are we working on today?
          </h1>
        </div>
        <WelcomeMascot className="h-28 w-28 sm:h-40 sm:w-40 lg:h-44 lg:w-44 drop-shadow-[0_14px_30px_rgba(37,99,255,0.35)]" />
      </div>
      <p className="text-neutral-400 mt-3 max-w-xl text-sm sm:text-base rise" style={{ animationDelay: "80ms" }}>
        Ask a question, paste a problem, or upload homework, notes and documents. I explain step by step — and remember what helps.
      </p>
      <div className="flex flex-wrap gap-2 mt-5">
        <button onClick={() => fire("composer-upload")} data-testid="welcome-upload-btn"
          className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-sky-400 text-white text-sm font-medium hover:bg-sky-500"><Upload className="h-4 w-4" /> Upload homework</button>
        <button onClick={() => fire("composer-focus")} data-testid="welcome-ask-btn"
          className="inline-flex items-center gap-2 h-11 px-5 rounded-full border border-white/15 text-sm hover:bg-white/5"><MessageCircleQuestion className="h-4 w-4 text-sky-300" /> Ask a question</button>
      </div>
      <div className="grid grid-cols-2 gap-2.5 mt-6">
        {STARTERS.map((s, i) => (
          <button key={s.text} onClick={() => onPick(s.text)} data-testid={`starter-${i}`} title={s.text} style={{ animationDelay: `${120 + i * 40}ms` }}
            className="rise text-left flex items-center gap-3 p-3.5 rounded-2xl border border-white/10 bg-white/[0.03] hover:bg-white/[0.06] hover:border-sky-400/30 transition-colors">
            <span className="h-8 w-8 rounded-xl bg-sky-400/15 flex items-center justify-center shrink-0"><s.icon className="h-4 w-4 text-sky-300" /></span>
            <span className="text-sm text-neutral-200 leading-snug">{s.label}</span>
          </button>
        ))}
      </div>
      <ExamCountdown />
    </div>
  );
}

function chatLimitReached(ent, voiceToken) {
  if (voiceToken || !ent || ent.chat_open !== false) return false;
  if (ent.resets_at && Date.now() >= new Date(ent.resets_at).getTime()) return false;
  window.dispatchEvent(new CustomEvent("paywall", { detail: { code: "limit", resets_at: ent.resets_at } }));
  return true;
}

function useChatSend({ chatId, view, navigate, refreshChats, refreshEnt, ent, setMessages }) {
  const [streamMsg, setStreamMsg] = useState(null);
  const [busy, setBusy] = useState(false);
  const viewRef = useRef(view);
  const mountedRef = useRef(true);
  const genRef = useRef(null);
  const streamRef = useRef(null);
  viewRef.current = view;
  streamRef.current = streamMsg;
  useEffect(() => { mountedRef.current = true; return () => { mountedRef.current = false; }; }, []);
  // Leaving only detaches the UI: the answer keeps generating on the server and Recents shows a dot when it's ready.
  useEffect(() => { setBusy(false); setStreamMsg(null); }, [view]);
  const stillOn = useCallback((v) => mountedRef.current && viewRef.current === v, []);
  const makeDoc = async ({ format, request }) => {
    if (chatLimitReached(ent, "")) return false;
    const origin = view;
    try {
      let id = chatId;
      if (!id) { id = (await http.post("/chats")).data.chat_id; }
      const { data } = await http.post(`/chats/${id}/documents`, { format, request });
      if (stillOn(origin)) setMessages((m) => [...m, data.user_message, data.message]);
      refreshChats();
      if (!chatId && stillOn(origin)) navigate(`/app/c/${id}`);
      return true;
    } catch (e) {
      if (e.response?.status !== 402) toast.error(formatError(e));
      return false;
    } finally { refreshEnt(); }
  };
  // Batch streamed text into ~12 renders a second so long answers don't make scrolling stutter.
  const pending = useRef({ text: "", timer: null });
  const pushDelta = (chunk) => {
    const p = pending.current;
    p.text += chunk;
    if (p.timer) return;
    p.timer = setTimeout(() => {
      const add = p.text;
      p.text = "";
      p.timer = null;
      if (add) setStreamMsg((s) => (s ? { ...s, content: s.content + add } : s));
    }, 80);
  };
  const send = async ({ text, files = [], mode = "normal", voiceToken = "", onDelta }) => {
    if (chatLimitReached(ent, voiceToken)) return false;
    const origin = view;
    const visible = () => stillOn(origin);
    // Each answer has its own id so Stop works instantly (even while Thinking) and old answers can't leak into new ones.
    const g = { id: newId(), ctrl: new AbortController(), chatId: chatId || null, view: origin, stopped: false };
    genRef.current = g;
    setBusy(true);
    let id = chatId;
    let done = null;
    let gotMeta = false;
    try {
      if (!id) {
        const { data } = await http.post("/chats");
        id = data.chat_id;
        refreshChats();
      }
      g.chatId = id;
      if (g.stopped) return false;
      if (visible()) {
        setMessages((m) => [...m, { message_id: "pending", role: "user", content: text, files, mode }]);
        setStreamMsg({ content: "", meta: null });
      }
      await streamChat(id, { text, file_ids: files.map((f) => f.file_id), mode, voice_token: voiceToken, gen_id: g.id }, (ev) => {
        if (g.stopped) return;
        if (ev.type === "meta") { gotMeta = true; refreshChats(); }
        if (!visible()) return;
        if (ev.type === "meta") {
          setMessages((m) => m.map((x) => (x.message_id === "pending" ? ev.user_message : x)));
          setStreamMsg((s) => ({ ...s, meta: ev.meta }));
        } else if (ev.type === "delta") { onDelta?.(ev.content); pushDelta(ev.content); }
        else if (ev.type === "done") { clearTimeout(pending.current.timer); pending.current = { text: "", timer: null }; done = ev.message; setMessages((m) => [...m, ev.message]); }
        else if (ev.type === "stopped") { clearTimeout(pending.current.timer); pending.current = { text: "", timer: null }; if (ev.message) setMessages((m) => [...m, ev.message]); stoppedToast(ev.charged); }
        else if (ev.type === "error") toast.error(ev.detail || "The tutor hit an error");
      }, g.ctrl.signal);
      // Watching when it finished counts as read; otherwise Recents keeps the "ready" dot. Never pull the student back here.
      if (done && visible()) await http.post(`/chats/${id}/seen`).catch((e) => logWarn("Couldn't mark the answer as read", e));
      refreshChats();
      if (!chatId && visible()) navigate(`/app/c/${id}`);
      return done || true;
    } catch (e) {
      if (g.stopped || isStopped(e)) {
        if (!chatId && id && visible()) navigate(`/app/c/${id}`);
        return false;
      }
      if (gotMeta) {
        // Connection dropped mid-answer: the server keeps going, so pick it up from there.
        if (visible()) { if (!chatId) navigate(`/app/c/${id}`); else window.dispatchEvent(new CustomEvent("chat-resume", { detail: { chatId: id } })); }
        return false;
      }
      if (!visible()) return false;
      if (!e.paywall) toast.error(e.message || "Failed to send");
      setMessages((m) => m.filter((x) => x.message_id !== "pending"));
      if (e.paywall && !chatId && id) await http.delete(`/chats/${id}`).then(refreshChats).catch((err) => logWarn("Couldn't remove empty chat", err));
      return false;
    } finally {
      if (genRef.current === g) genRef.current = null;
      if (visible() && !g.stopped) { setStreamMsg(null); setBusy(false); }
      refreshEnt();
    }
  };
  // Stop: the UI resets at once (keeping any words already written); the server cancels and settles the charge.
  const stop = async () => {
    const g = genRef.current;
    const id = g?.chatId || chatId;
    const here = g?.view || view;
    if (g) { g.stopped = true; genRef.current = null; g.ctrl.abort(); }
    const p = pending.current;
    clearTimeout(p.timer);
    const written = (streamRef.current?.content || "") + p.text;
    pending.current = { text: "", timer: null };
    if (g && written.trim()) setMessages((m) => [...m, { message_id: `stopped-${g.id}`, role: "assistant", content: `${written}\n\n_(Stopped)_`, meta: streamRef.current?.meta, stopped: true }]);
    setStreamMsg(null);
    setBusy(false);
    if (!id) return;
    const showing = () => stillOn(here) || stillOn(id);
    try {
      const { data } = await http.post(`/chats/${id}/stop`, null, { params: g ? { gen_id: g.id } : {} });
      stoppedToast(data.charged);
      if (showing()) {
        const c = await http.get(`/chats/${id}`);
        if (showing()) setMessages(c.data.messages);
      }
    } catch (e) { logWarn("Stop failed", e); } finally { refreshEnt(); }
  };

  return { send, makeDoc, busy, streamMsg, stop, stillOn };
}

// Shows an answer that is still being generated on the server (after switching chats or reconnecting).
function useResume(chatId, active, setMessages) {
  const [msg, setMsg] = useState(null);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const h = (e) => e.detail.chatId === chatId && setTick((t) => t + 1);
    window.addEventListener("chat-resume", h);
    return () => window.removeEventListener("chat-resume", h);
  }, [chatId]);
  useEffect(() => {
    setMsg(null);
    if (!chatId || active) return undefined;
    let alive = true, timer, seen = false;
    const poll = () => http.get(`/chats/${chatId}/generation`).then((r) => {
      if (!alive) return;
      if (r.data.status === "running") {
        seen = true;
        setMsg({ content: r.data.content || "", meta: null });
        timer = setTimeout(poll, 1500);
      } else {
        setMsg(null);
        if (seen) http.get(`/chats/${chatId}`).then((c) => alive && setMessages(c.data.messages)).catch((e) => logWarn("Chat reload failed", e));
      }
    }).catch((e) => { logWarn("Generation status failed", e); if (alive) timer = setTimeout(poll, 4000); });
    poll();
    return () => { alive = false; clearTimeout(timer); };
  }, [chatId, active, tick, setMessages]);
  return msg;
}

// Follows new content only while the reader is at the bottom; scrolling up pauses it until "Jump to latest".
function useStickToBottom(contentDeps, resetDeps) {
  const ref = useRef(null);
  const atBottomRef = useRef(true);
  const jumpingRef = useRef(false);
  const [atBottom, setAtBottom] = useState(true);
  const set = (v) => { atBottomRef.current = v; setAtBottom(v); };
  const toBottom = (smooth) => { const el = ref.current; if (el) el.scrollTo({ top: el.scrollHeight, behavior: smooth ? "smooth" : "auto" }); };
  const onScroll = () => {
    const el = ref.current;
    if (!el) return;
    const near = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    if (jumpingRef.current) { if (near) jumpingRef.current = false; return; }
    if (near !== atBottomRef.current) set(near);
  };
  useEffect(() => { set(true); requestAnimationFrame(() => toBottom(false)); }, resetDeps); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (atBottomRef.current) toBottom(false); }, contentDeps); // eslint-disable-line react-hooks/exhaustive-deps
  const jump = () => { jumpingRef.current = true; set(true); toBottom(true); };
  return { ref, onScroll, atBottom, jump };
}

export default function ChatPage() {
  const { chatId } = useParams();
  const navigate = useNavigate();
  const { refreshChats } = useOutletContext();
  const { user, refreshEnt, ent } = useAuth();
  const [messages, setMessages] = useState([]);
  const endRef = useRef(null);
  const location = useLocation();
  // Each visit to a new chat is its own view, so an answer started there never drags you back after you move on.
  const view = chatId || `new:${location.key}`;
  const prefilled = useRef(false);
  const { send: sendChat, makeDoc, busy: sending, streamMsg: liveMsg, stop, stillOn } = useChatSend({ chatId, view, navigate, refreshChats, refreshEnt, ent, setMessages });
  const resumed = useResume(chatId, sending, setMessages);
  const [hideResume, setHideResume] = useState(false);
  useEffect(() => setHideResume(false), [chatId]);
  const resumeMsg = hideResume ? null : resumed;
  const streamMsg = liveMsg || resumeMsg;
  const busy = sending || !!resumeMsg;
  const stopAll = () => {
    if (resumeMsg) setHideResume(true);
    stop().finally(() => { setHideResume(false); window.dispatchEvent(new CustomEvent("chat-resume", { detail: { chatId } })); });
  };
  const reload = useCallback(() => {
    if (chatId) http.get(`/chats/${chatId}`).then((r) => setMessages(r.data.messages)).catch((e) => logWarn("Chat reload failed", e));
    refreshEnt();
  }, [chatId, refreshEnt]);
  const video = useVideoFlow({
    ensureChat: async () => chatId || (await http.post("/chats")).data.chat_id,
    onStarted: (id, userMsg) => {
      refreshChats();
      refreshEnt();
      if (!stillOn(view)) return;
      setMessages((m) => [...m, userMsg]);
      if (!chatId) navigate(`/app/c/${id}`);
    },
  });
  const fromLinkRef = useRef(video.fromLink);
  fromLinkRef.current = video.fromLink;
  useEffect(() => {
    const h = (e) => fromLinkRef.current(e.detail.url, "");
    window.addEventListener("video-retry", h);
    return () => window.removeEventListener("video-retry", h);
  }, []);
  const setGuideRef = useRef(video.setGuide);
  setGuideRef.current = video.setGuide;
  useEffect(() => {
    const h = (e) => setGuideRef.current({ url: e.detail.url });
    window.addEventListener("video-guide", h);
    return () => window.removeEventListener("video-guide", h);
  }, []);
  const send = async (args) => {
    const link = !args.files?.length && (args.mode || "normal") === "normal" && !args.voiceToken && args.text?.match(VIDEO_URL);
    if (!link) return sendChat(args);
    return video.fromLink(link[0], args.text.replace(link[0], "").trim());
  };

  useEffect(() => {
    if (!chatId) { setMessages([]); return; }
    http.get(`/chats/${chatId}`).then((r) => setMessages(r.data.messages)).catch(() => navigate("/app"));
  }, [chatId, view, navigate]);

  const lastRole = messages[messages.length - 1]?.role;
  const stick = useStickToBottom([messages, streamMsg?.content], [chatId, lastRole === "user" ? messages.length : 0]);

  const onFeedback = async (msg, value) => {
    try {
      await http.post(`/messages/${msg.message_id}/feedback`, { value });
      setMessages((m) => m.map((x) => (x.message_id === msg.message_id ? { ...x, feedback: value } : x)));
      if (value === "helpful") toast.success("Thanks! I'll remember what worked.");
      else send({ text: "Please re-check it.", mode: "retry" });
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  const sendRef = useRef(send);
  sendRef.current = send;
  useEffect(() => {
    const p = location.state?.prefill;
    if (!p || chatId || prefilled.current) return;
    prefilled.current = true;
    window.history.replaceState({}, "");
    sendRef.current({ text: p.text, files: p.files || [] });
  }, [location.state, chatId]);

  const empty = !chatId && !messages.length && !streamMsg;

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      <ChatActions chatId={chatId} hasAnswers={messages.some((m) => m.role === "assistant")} />
      <div className="flex-1 min-h-0 relative flex flex-col">
      <div ref={stick.ref} onScroll={stick.onScroll} className="flex-1 overflow-y-auto scroll-thin" data-testid="chat-messages">
        {empty ? (
          <Empty name={user?.name} onPick={(t) => send({ text: t })} />
        ) : (
          <div className="max-w-3xl mx-auto px-4 sm:px-5 py-6 sm:py-8 space-y-8">
            {messages.map((m) =>
              m.role === "user" ? <UserMessage key={m.message_id} msg={m} onVideoDone={reload} /> : (
                <AssistantMessage key={m.message_id} msg={m} disabled={busy} onFeedback={onFeedback}
                  onConfused={() => send({ text: "Can you explain it another way?", mode: "confused" })} />
              )
            )}
            {streamMsg && <AssistantMessage msg={streamMsg} streaming />}
            <div ref={endRef} />
          </div>
        )}
      </div>
      {busy && (
        <button onClick={stopAll} data-testid="stop-generating-btn"
          className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 rounded-full bg-[#121725]/95 border border-white/15 px-3 py-1.5 text-xs text-neutral-300 shadow-lg backdrop-blur hover:bg-white/10 transition-colors">
          <Square className="h-3 w-3 fill-current" /> Stop
        </button>
      )}
      {!stick.atBottom && !empty && (
        <button onClick={stick.jump} data-testid="jump-to-latest-btn"
          className="absolute bottom-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 rounded-full bg-[#121725]/95 border border-white/15 px-3.5 py-1.5 text-xs text-neutral-200 shadow-lg backdrop-blur hover:bg-white/10 transition-colors rise">
          <ArrowDown className="h-3.5 w-3.5" /> Jump to latest
        </button>
      )}
      </div>
      <VideoConfirmDialog flow={video} />
      <Composer onSend={send} onMakeDoc={makeDoc} onVideoFile={video.fromFile} videoProgress={video.progress} videoGuide={video.guide} onCloseGuide={() => video.setGuide(null)} busy={busy} onStop={stopAll} draft={!chatId ? location.state?.draft : undefined} />
    </div>
  );
}
